import React, { useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  ButtonBase,
  IconButton,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { TOKENS } from '../theme';
import { VOID_REASONS } from '../../raffle/raffleTokens';

// Raffle 1 spec R54–R56 — a raffle is never deleted, it is voided with a
// reason. The row stays in the record and stops counting in every total.
//
// This is the only destructive action in the Raffle Report, and it is taken
// against money that has already been handed over, so the dialog is built to
// be checked rather than clicked through: it shows the payout it is about to
// strike out — customer, number, drawing and amount — with the amount already
// rendered struck through, so the outcome is visible before it is confirmed.
// The reasons are targets rather than a radio list because on a counter
// tablet a mis-tap here is a wrongly voided payout.
export default function VoidRaffleDialog({
  open,
  onClose,
  onConfirm,
  title,
  subtitle,
  entry = {},
}) {
  const [reason, setReason] = useState('');

  const amount = entry.amount != null ? `$${entry.amount}` : subtitle || '';
  const numberLine = [
    entry.winnerNumber ? `#${entry.winnerNumber}` : null,
    entry.colorName || null,
    entry.mode === 'machine' ? 'Machine' : entry.drawingName || null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      {/* Header — red enough to read as destructive without drowning the
          content that has to be checked. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          p: 2.5,
          pb: 2,
          background: TOKENS.red50,
          borderBottom: `1px solid ${TOKENS.red100}`,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: '50%',
            background: TOKENS.red100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BlockIcon sx={{ color: TOKENS.red600, fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{ fontSize: 17, fontWeight: 700, color: TOKENS.slate900 }}
          >
            {title}
          </Typography>
          <Typography sx={{ fontSize: 12, color: TOKENS.red700, mt: 0.25 }}>
            This payout will stop counting everywhere.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon sx={{ fontSize: 18, color: TOKENS.slate500 }} />
        </IconButton>
      </Box>

      <Box sx={{ p: 2.5, pt: 2 }}>
        {/* What is about to be struck out. The amount is shown struck
            through already — the dialog previews its own outcome. */}
        <Box
          sx={{
            border: `1px solid ${TOKENS.slate200}`,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            background: TOKENS.slate50,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: TOKENS.slate900,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {entry.name || subtitle || '—'}
            </Typography>
            {numberLine && (
              <Typography
                sx={{
                  fontSize: 11,
                  fontFamily: 'monospace',
                  color: TOKENS.slate500,
                  mt: 0.25,
                }}
              >
                {numberLine}
              </Typography>
            )}
          </Box>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 800,
              fontFamily: 'monospace',
              color: TOKENS.red600,
              textDecoration: 'line-through',
              flexShrink: 0,
            }}
          >
            {amount}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.4,
            color: TOKENS.slate500,
            mt: 2.5,
            mb: 1,
          }}
        >
          WHY IS IT BEING VOIDED?
        </Typography>

        {/* Tap targets rather than a radio list — a mis-tap here voids the
            wrong payout, and these are used on a counter tablet. */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {VOID_REASONS.map((r) => {
            const on = reason === r;
            return (
              <ButtonBase
                key={r}
                onClick={() => setReason(r)}
                sx={{
                  justifyContent: 'flex-start',
                  gap: 1.25,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 2,
                  border: `1px solid ${on ? TOKENS.red300 : TOKENS.slate200}`,
                  background: on ? TOKENS.red50 : '#fff',
                  transition: 'background 120ms, border-color 120ms',
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    flexShrink: 0,
                    borderRadius: '50%',
                    border: `2px solid ${on ? TOKENS.red600 : TOKENS.slate200}`,
                    background: on ? TOKENS.red600 : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {on && <CheckIcon sx={{ fontSize: 13, color: '#fff' }} />}
                </Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: on ? 700 : 500,
                    color: on ? TOKENS.red700 : TOKENS.slate700,
                  }}
                >
                  {r}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>

        {/* The consequence, set apart from the explanation so it is not read
            past. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            mt: 2,
            p: 1.25,
            borderRadius: 2,
            background: TOKENS.amber50,
            border: `1px solid ${TOKENS.amber200}`,
          }}
        >
          <WarningAmberIcon
            sx={{ fontSize: 16, color: TOKENS.amber700, mt: '1px' }}
          />
          <Typography sx={{ fontSize: 11.5, color: TOKENS.amber900 }}>
            The raffle stays on the record, crossed out and showing this reason,
            and drops out of the shift and weekly totals.{' '}
            <strong>This cannot be undone.</strong>
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mt: 2.5 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth
            sx={{
              borderColor: TOKENS.slate200,
              color: TOKENS.slate700,
              fontWeight: 600,
              borderRadius: 2,
              py: 1.1,
              '&:hover': {
                borderColor: TOKENS.slate400,
                background: TOKENS.slate50,
              },
            }}
          >
            Keep it
          </Button>
          <Button
            onClick={() => onConfirm(reason)}
            disabled={!reason}
            variant="contained"
            fullWidth
            disableElevation
            sx={{
              background: TOKENS.red600,
              fontWeight: 700,
              borderRadius: 2,
              py: 1.1,
              '&:hover': { background: TOKENS.red700 },
              '&.Mui-disabled': {
                background: TOKENS.slate100,
                color: TOKENS.slate400,
              },
            }}
          >
            Void raffle
          </Button>
        </Box>
        {/* Says why the button is dead rather than leaving it greyed and
            unexplained. */}
        {!reason && (
          <Typography
            sx={{
              fontSize: 11,
              color: TOKENS.slate400,
              textAlign: 'center',
              mt: 1,
            }}
          >
            Pick a reason to continue
          </Typography>
        )}
      </Box>
    </Dialog>
  );
}
